import { LoginFormValues } from 'Interfaces/LoginRegisterFormProps';
import { ValidationErrors } from 'Interfaces/ValidationErrors';
import { OnFailure, OnFailureData, OnSuccess, OnSuccessData } from 'Interfaces/Api';
import { ClubRegistrationStatus, LoginResponse } from 'Types/types';
import { apiGet, apiPost, CallFetchOptions, FetchError, FetchResponse } from 'SharedHelpers/apiFetch';
import { RegisterUserValues } from 'Interfaces/UseApiReturnType';

const SIGNUP_URL = 'user/register';
const LOGIN_URL = 'user/login';
const CMS_URL = 'cms';

export const REGISTRATION_SOURCE = 'registration_overlay';
export const REGISTRATION_PLATFORM = 'mobile';

const IS_EXISTING_USER = 'isUserAlreadyExisting';
const ACCEPTED_NEWSLETTER_CONFIRMED = 'accepted_newsletter_confirmed';

export const NEW_USER_MESSAGE = 'CUSTOMER_REGISTRATION_SUCCESS';
export const EXISTING_USER_MESSAGE = 'Thank you! You are already a Westwing member.';
export const PRIVACY_POLICY_STATIC_PAGE_KEY = 'privacy_policy';

interface PartialGetCmsResponse {
    // eslint-disable-next-line camelcase
    data?: { items?: { text_not_stripped: string } };
}

export default class ShopApi {
    private readonly shopApiBaseUrl: string;

    private readonly buildVersion: string;

    private readonly country: string;

    private readonly apiPostSignupLogin = async <E = {}, B extends {} = {}>(
        url: string,
        body?: B,
        options: Exclude<CallFetchOptions, 'body'> = {}
    ): Promise<FetchResponse<LoginResponse>> =>
        apiPost<LoginResponse, E>(url, body, {
            headers: this._getHeaders(),
            ...options,
        }).catch(this._existingUserErrorInterceptor);

    public constructor(shopApiBaseUrl: string, buildVersion: string, country: string) {
        this.shopApiBaseUrl = shopApiBaseUrl;
        this.buildVersion = buildVersion;
        this.country = country;
    }

    private _existingUserErrorInterceptor = (error: FetchError<LoginResponse>) =>
        // Whe need this error as a standard response.
        {
            if (error.response?.status === 400 && error?.data) {
                return new FetchResponse<LoginResponse>(error.response, error.data);
            }
            throw error;
        };

    private _getHeaders = (headers: object = {}): Headers => {
        const g: typeof globalThis & { navigator?: Navigator } = global;
        return new Headers({
            Accept: 'application/json',
            'x-app-version': `1.0.0-${this.buildVersion}`,
            'x-app-type': 'bella',
            'x-device-type': 'phone',
            'x-client-user-agent': g.navigator ? window.navigator.userAgent : '',
            ...headers,
        });
    };

    private _getAbsoluteUrl = (url: string): string => `${this.shopApiBaseUrl}/${url}?country=${this.country}`;

    private _getSignupUrl = (): string => this._getAbsoluteUrl(SIGNUP_URL);

    private _getLoginUrl = (): string => this._getAbsoluteUrl(LOGIN_URL);

    private _getCmsUrl = (pageKey: string): string => this._getAbsoluteUrl(`${CMS_URL}/${pageKey}`);

    private _validateResponse = (json: LoginResponse, accessField = 'isLoggedIn'): boolean =>
        typeof json === 'object' && json?.data?.[accessField];

    private _getValidationErrors = (loginError: ValidationErrors | Error | undefined): ValidationErrors | {} => {
        if (!(loginError instanceof Error) && !Array.isArray(loginError)) {
            return {
                email: loginError?.email,
                password: loginError?.password,
            };
        }
        return {};
    };

    private _register = async (userValues: RegisterUserValues): Promise<FetchResponse<LoginResponse>> =>
        this.apiPostSignupLogin(this._getSignupUrl(), {
            registrationSource: REGISTRATION_SOURCE,
            registrationPlatform: REGISTRATION_PLATFORM,
            ...userValues,
        });

    private _login = async (username: string, password: string): Promise<FetchResponse<LoginResponse>> =>
        this.apiPostSignupLogin(this._getLoginUrl(), { username, password });

    public loginUser = async (
        username: string,
        password: string,
        fromRegistration = false,
        onSuccess: OnSuccess,
        onFailureData: OnFailureData<ValidationErrors | {}>
    ): Promise<void> => {
        try {
            const { data } = await this._login(username, password);
            const loginStatus = this._validateResponse(data);
            if (fromRegistration) {
                // TODO Track login via registration.
            }
            if (loginStatus) {
                // TODO Track successful login
                onSuccess();
                return;
            }
            onFailureData(this._getValidationErrors(data.data.errors));
        } catch (e) {
            if (fromRegistration) {
                // TODO Track login via registration.
            }
            onFailureData(e);
        }
    };

    public registerNewUser = async (
        formValues: RegisterUserValues,
        onSuccess: OnSuccess,
        onFailureData: OnFailureData<ValidationErrors | {}>
    ): Promise<void> => {
        try {
            const { data } = await this._register(formValues);
            const loginStatus = this._validateResponse(data);
            if (loginStatus) {
                if (formValues.isNewsletterSubscribed) {
                    // TODO Handle newsletter subscription.
                }
                // TODO Track registration with newsletter flag.
                onSuccess();
                return;
            }
            if (data?.data?.isUserAlreadyExisting) {
                await this.loginUser(
                    formValues.email,
                    formValues.password,
                    true,
                    () => ({}),
                    () => ({})
                );
                onSuccess();
                return;
            }
            onFailureData(this._getValidationErrors(data.data.errors));
        } catch (e) {
            onFailureData(e);
        }
    };

    public registerNewUserToClub = async (
        formValues: RegisterUserValues,
        onSuccessData: OnSuccessData<ClubRegistrationStatus>,
        onFailureData: OnFailureData<ValidationErrors | {}>
    ): Promise<void> => {
        try {
            const { data } = await this._register(formValues);
            const isExistingUser = this._validateResponse(data, IS_EXISTING_USER);
            const registrationStatus = this._validateResponse(data);
            if (isExistingUser) {
                await this.handleExistingUserClubRegistration(formValues, onSuccessData, onFailureData);
                return;
            }
            if (registrationStatus) {
                onSuccessData(ClubRegistrationStatus.NEW_REGISTRATION);
            } else {
                onFailureData(this._getValidationErrors(data.data.errors));
            }
        } catch (e) {
            onFailureData(e);
        }
    };

    public handleExistingUserClubRegistration = async (
        formValues: LoginFormValues,
        onSuccessData: OnSuccessData<ClubRegistrationStatus>,
        onFailureData: OnFailureData<ValidationErrors | {}>
    ): Promise<void> => {
        try {
            const { data } = await this._login(formValues.email, formValues.password);
            const loginStatus = this._validateResponse(data);
            if (loginStatus) {
                const isAcceptedNewsletterConfirmed = this._validateResponse(data, ACCEPTED_NEWSLETTER_CONFIRMED);
                const status = isAcceptedNewsletterConfirmed
                    ? ClubRegistrationStatus.EXISTING_USER_SUBSCRIBED
                    : ClubRegistrationStatus.EXISTING_USER_NOT_SUBSCRIBED;
                onSuccessData(status);
                return;
            }
            onFailureData(this._getValidationErrors(data.data.errors));
        } catch (e) {
            onFailureData(e);
        }
    };

    public getCmsStaticPage = async (
        pageKey: string,
        onSuccessData: OnSuccessData<string>,
        onFailure: OnFailure
    ): Promise<void> => {
        try {
            // TODO Using shopApi to get causing DOMException: Invalid header name.
            const { data } = await apiGet<PartialGetCmsResponse>(this._getCmsUrl(`${pageKey}/staticpage`));
            onSuccessData(data?.data?.items?.text_not_stripped as string);
        } catch (e) {
            onFailure();
        }
    };
}
